import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Shield, Mail, Calendar, Activity } from 'lucide-react';

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  email_confirmed_at: string | null;
  roles: string[];
  is_admin: boolean;
  mfa_enabled: boolean;
  activity: {
    downloads: number;
    assessments: number;
    total: number;
  };
}

interface UserDetailsDialogProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserDetailsDialog = ({ user, open, onOpenChange }: UserDetailsDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            User Details
          </DialogTitle>
          <DialogDescription>
            Complete information for {user.email}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Account Information
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Email:</span>
                <p className="font-medium mt-1">{user.email}</p>
              </div>
              <div>
                <span className="text-muted-foreground">User ID:</span>
                <p className="font-mono text-xs mt-1">{user.id}</p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Status & Roles
            </h3>
            <div className="flex flex-wrap gap-2">
              {user.is_admin && (
                <Badge variant="default">Admin</Badge>
              )}
              {user.email_confirmed_at ? (
                <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/20">
                  Email Verified
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                  Email Unverified
                </Badge>
              )}
              {user.mfa_enabled && (
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/20">
                  MFA Enabled
                </Badge>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Timeline
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Joined:</span>
                <p className="font-medium mt-1">
                  {new Date(user.created_at).toLocaleString()}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Last Sign In:</span>
                <p className="font-medium mt-1">
                  {user.last_sign_in_at
                    ? new Date(user.last_sign_in_at).toLocaleString()
                    : 'Never'}
                </p>
              </div>
              {user.email_confirmed_at && (
                <div>
                  <span className="text-muted-foreground">Email Confirmed:</span>
                  <p className="font-medium mt-1">
                    {new Date(user.email_confirmed_at).toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Activity */}
          <div className="space-y-3">
            <h3 className="font-semibold">Activity Summary</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-primary">
                  {user.activity.downloads}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Resource Downloads
                </div>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-primary">
                  {user.activity.assessments}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Assessments
                </div>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <div className="text-2xl font-bold text-primary">
                  {user.activity.total}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Total Actions
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};